import { Button } from "../components/Button";
import { ImageGenerationPlaceholder } from "../components/ImageGenerationPlaceholder";
import { ImageWrapper } from "../components/ImageWrapper";
import { downloadFile, ImageZoomDialog, } from "../components/ImageZoomDialog";
import { Spinner } from "../components/Spinner";
import { Download01, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React, { useCallback, useState } from "react";
export const IMAGE_PREVIEW_VARIANTS = ["embedded", "standalone"];
export const IMAGE_PREVIEW_TITLE_POSITIONS = ["bottom", "center"];
const containerVariants = cva(cn("cursor-pointer overflow-hidden rounded-2xl"), {
    variants: {
        variant: {
            // Embedded inside a parent component (like Citation) that provides the group
            embedded: "absolute inset-0",
            // Standalone, self-contained component managing its own hover
            standalone: cn("group/image-preview", "relative aspect-square", "bg-muted-background"),
        },
    },
    defaultVariants: {
        variant: "embedded",
    },
});
const overlayVariants = cva(cn("absolute inset-0 z-10", "bg-primary-100/60", "opacity-0 transition duration-200"), {
    variants: {
        titlePosition: {
            bottom: cn("flex flex-col items-start justify-end", "px-3 pb-7"),
            center: "flex items-center justify-center",
        },
        variant: {
            // Embedded: uses parent's group for hover
            embedded: "group-hover:opacity-100",
            // Standalone: uses its own group/image-preview
            standalone: "group-hover/image-preview:opacity-100",
        },
    },
    defaultVariants: {
        titlePosition: "bottom",
        variant: "embedded",
    },
});
const titleVariants = cva(cn("max-w-full truncate", "heading-sm", "text-foreground"), {
    variants: {
        titlePosition: {
            bottom: "",
            center: "max-w-[90%] px-2 text-center",
        },
    },
    defaultVariants: {
        titlePosition: "bottom",
    },
});
const ImagePreview = React.forwardRef(({ imgSrc, alt = "", title = "", downloadUrl, isLoading, isGenerating, onClose, onClick, className, variant = "embedded", titlePosition = "bottom", manageZoomDialog = true, }, ref) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleDownload = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (downloadUrl && title) {
            downloadFile(downloadUrl, title);
        }
    }, [downloadUrl, title]);
    const handleClose = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        onClose === null || onClose === void 0 ? void 0 : onClose(e);
    }, [onClose]);
    const handleImageClick = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isLoading && !isGenerating) {
            if (onClick) {
                onClick(e);
            }
            else if (manageZoomDialog) {
                setIsDialogOpen(true);
            }
        }
    }, [isLoading, isGenerating, onClick, manageZoomDialog]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: ref, onClick: handleImageClick, className: cn(containerVariants({ variant }), className) }, isGenerating ? (React.createElement(ImageGenerationPlaceholder, { fill: true })) : isLoading ? (React.createElement("div", { className: cn("flex h-full w-full items-center justify-center", "bg-muted-background") },
            React.createElement(Spinner, { variant: "dark", size: "md" }))) : (React.createElement(React.Fragment, null,
            React.createElement(ImageWrapper, { src: imgSrc, alt: alt, className: cn("h-full w-full object-cover transition duration-200", variant === "embedded"
                    ? "group-hover:blur-sm"
                    : "group-hover/image-preview:blur-sm") }),
            React.createElement("div", { className: overlayVariants({ titlePosition, variant }) },
                React.createElement("span", { className: titleVariants({ titlePosition }) }, title)),
            React.createElement("div", { className: cn("absolute right-2 top-2 z-20", "opacity-0 transition-opacity duration-200", variant === "embedded"
                    ? "group-hover:opacity-100"
                    : "group-hover/image-preview:opacity-100") },
                onClose && (React.createElement(Button, { variant: "ghost", size: "mini", icon: XClose, tooltip: "Remove", onClick: handleClose })),
                !onClose && downloadUrl && (React.createElement(Button, { variant: "ghost", size: "mini", icon: Download01, tooltip: "Download", onClick: handleDownload })))))),
        manageZoomDialog && (React.createElement(ImageZoomDialog, { open: isDialogOpen, onOpenChange: setIsDialogOpen, image: {
                src: imgSrc,
                alt,
                title,
                downloadUrl,
                isLoading,
            } }))));
});
ImagePreview.displayName = "ImagePreview";
export { ImagePreview };
//# sourceMappingURL=ImagePreview.js.map